import React, { useCallback, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Field, Input } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { messageErreur, statutErreur } from '../../lib/reservationsApi';

/**
 * Code manager : une action protégée par la direction répond « 403 … code manager » ; on demande alors les 4 chiffres et on
 * rejoue l'action avec le code. Mauvais code : le message reste dans la fenêtre. Trop d'essais (429) : la fenêtre se ferme et
 * l'erreur remonte à l'écran. Annuler la fenêtre : l'action n'est pas faite (la promesse rend `undefined`).
 */
const demandeLeCode = (e: unknown) => statutErreur(e) === 403 && /code manager/i.test(messageErreur(e));

interface Demande { fn: (code: string) => Promise<unknown>; resolve: (v: unknown) => void; reject: (e: unknown) => void; message: string }

export const useCodeManager = () => {
    const [demande, setDemande] = useState<Demande | null>(null);
    const [code, setCode] = useState('');
    const [erreur, setErreur] = useState('');
    const [envoi, setEnvoi] = useState(false);

    /** Lance `fn` sans code ; si le serveur en demande un, ouvre la fenêtre et rejoue. */
    const executer = useCallback(async <T,>(fn: (code?: string) => Promise<T>): Promise<T | undefined> => {
        try { return await fn(); } catch (e) {
            if (!demandeLeCode(e)) throw e;
            return new Promise<T | undefined>((resolve, reject) => {
                setCode(''); setErreur(''); setDemande({ fn: fn as (c: string) => Promise<unknown>, resolve: resolve as (v: unknown) => void, reject, message: messageErreur(e) });
            });
        }
    }, []);

    const fermer = () => { demande?.resolve(undefined); setDemande(null); };
    const valider = async () => {
        if (!demande) return;
        setEnvoi(true); setErreur('');
        try { const r = await demande.fn(code); demande.resolve(r); setDemande(null); }
        catch (e) {
            if (demandeLeCode(e)) setErreur(messageErreur(e));
            else { demande.reject(e); setDemande(null); }
        } finally { setEnvoi(false); }
    };

    const fenetre = (
        <Modal open={!!demande} onClose={fermer} size="sm" title="Code manager"
            footer={<><button type="button" className={pillSecondary(36)} onClick={fermer}>Annuler</button>
                <button type="button" className={pillPrimary(36)} disabled={envoi || !/^\d{4}$/.test(code)} onClick={() => void valider()}>Valider</button></>}>
            <form onSubmit={(e) => { e.preventDefault(); if (/^\d{4}$/.test(code)) void valider(); }}>
                <p className="text-[13px] text-content-muted mb-3">{demande?.message.replace(/^Cette action est protégée : /, 'Action protégée : ')}</p>
                <Field label="Code à 4 chiffres" error={erreur}>
                    {(f) => <Input {...f} type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={code} data-autofocus onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))} />}
                </Field>
            </form>
        </Modal>
    );
    return { executer, fenetre };
};
